/**
 * `Shell` — the app frame. `docs/design-direction.md` §5.2, §5.7, §9.1–§9.2;
 * `spec.md` §13.1.
 *
 * One element owns the whole geometry of the Mini App, and it owns exactly four
 * things:
 *
 *  1. **The height, from Telegram.** `block-size: var(--app-h)` with `overflow:
 *     hidden`, where `--app-h` is written from `viewportStableHeight` by the façade's
 *     `syncViewport` (called once at boot by `main.tsx`, which then keeps it in sync
 *     through Telegram's own geometry events). Never `100vh`: inside Telegram's
 *     webview that includes the strip under Telegram's header, so the shell overflows
 *     and the bottom of the mini player becomes unreachable. Never `viewportHeight`
 *     either — it jitters mid-drag and shears the layout.
 *  2. **The column.** Single column, 16px gutter (12px below 360px), capped at 480px
 *     and pinned **left of centre by −8px** on a wide viewport, which is what stops
 *     desktop Telegram from looking like a centred card (§5.2).
 *  3. **The scroll container and the dock.** Exactly one scroller, so `position:
 *     sticky` has a scrollport and the dock can overlay it; the mini player and the
 *     nav strip sit on top of the scrolling content at the documented z-layers, and
 *     the scroller pads `calc(100px + env(safe-area-inset-bottom))` for them (§9.2).
 *  4. **The grain.** Mounted once, here, and portalled to `<body>` by the `Grain`
 *     primitive — a blended fixed overlay nested inside a scroll container creates a
 *     compositing layer that can break `position: sticky` in older webviews (§6.13).
 *
 * ## Why the header, the dock and the sheet are slots
 *
 * They are `ReactNode` props, not imports. The mini player, the nav strip and every
 * sheet are components other tasks own, and a frame that imports its children can
 * only be tested with all of them present. Slots also make the two rules that matter
 * enforceable in one place: there is **one** dock and **one** sticky header per
 * screen, and both are positioned by this file rather than by whoever fills them.
 *
 * The `Sheet` primitive portals to `<body>` on its own (it must: `.halftone` sets
 * `isolation: isolate`, and a fixed layer nested inside would be trapped under it), so
 * the `sheet` slot is a mounting point in the tree rather than a positioned region —
 * one place a screen's sheet is rendered from, above the dock in DOM order.
 *
 * **Nothing here reads `themeParams`.** The app is a dark art-directed object and a
 * light-theme user gets the same crate; the only thing taken from Telegram is chrome
 * colour *assignment*, which `telegram.claimChrome()` does at boot (§10.1).
 */

import type { JSX, ReactNode } from 'react';

import { Grain } from '../components/primitives/Grain';
import { cx } from '../components/primitives/Rule';

import styles from './states/states.module.css';

export interface ShellProps {
  /** The screen. Usually `<Router />`. */
  readonly children?: ReactNode;
  /**
   * The 48px sticky header: mono caps left, count right, never centred, 1px
   * `--border-strong` bottom edge (§8's screen contract). Omitted screens get no
   * header rather than an empty bar.
   */
  readonly header?: ReactNode;
  /**
   * The bottom chrome: the 56px mini player above the 44px nav strip (§9.1, §9.2).
   * Composed by the entry point, because both components belong to another task.
   */
  readonly dock?: ReactNode;
  /** Mounting point for the screen's `Sheet`, which portals to `<body>` itself. */
  readonly sheet?: ReactNode;
  /**
   * The one-shot NEEDLE DROP grain flick — `.055 → .13 → .055` over `--t32`, fired on
   * play-from-stopped and **never on resume** (§7.2).
   */
  readonly grainFlick?: boolean;
  /** Lifts the grain to `.13` while audio is stalled mid-track (§8.8). */
  readonly buffering?: boolean;
  readonly className?: string | undefined;
}

/** The id the skip target and `aria-controls` references use. */
export const MAIN_ID = 'bl-main';

export function Shell({
  children,
  header,
  dock,
  sheet,
  grainFlick = false,
  buffering = false,
  className,
}: ShellProps): JSX.Element {
  return (
    <div
      className={cx(styles.shell, className)}
      data-shell="true"
      /* The scroller pads 100px + the safe-area inset for the mini player and the nav
         strip. On `/now` no dock is rendered at all, so that padding was 100px of dead
         air under the transport on the one screen the product is judged by. A plain
         attribute rather than `:has(...)`, which older Telegram webviews do not have.
         `data-has-dock` and NOT `data-dock`: `[data-dock]` is the dock element's own
         marker, and acceptance 6/7 asserts that selector matches nothing when no dock
         is passed. */
      data-has-dock={dock === undefined ? 'false' : 'true'}
    >
      <div className={styles.scroll} data-scroll="true">
        <div className={styles.column} data-column="true">
          {header === undefined ? null : (
            <div className={styles.header} data-header="true">
              {header}
            </div>
          )}
          <main id={MAIN_ID}>{children}</main>
        </div>
      </div>

      {dock === undefined ? null : (
        <div className={styles.dock} data-dock="true">
          {dock}
        </div>
      )}

      {sheet}

      <Grain flick={grainFlick} buffering={buffering} />
    </div>
  );
}
